import { z } from 'zod';
import { asRec, isRec, sinDuplicados, toStr, toStrList } from './adaptHelpers';
import { createDefaultPrefs } from './prefsDefaults';
import { normalizarPerfil } from './schemas';
import type {
  Favorito,
  PerfilTipo,
  PreferenciasConfig,
  WidgetConfig,
  WidgetParams,
  WidgetTipo,
} from './types';

/**
 * Conversión de PreferenciasConfig ⇄ forma del servidor (snake_case, docs/CONTRATO_API.md §2.4).
 * Lo que llega de la nube es DATO NO CONFIABLE: se sanea campo por campo (tipos, rangos y tamaños) y se
 * completa con los valores por defecto. Las claves dentro de `params` y de `rendimiento_por_categoria`
 * son datos, no nombres de campo: nunca se renombran.
 */
export const WIDGET_TIPOS = [
  'bcv',
  'resumen',
  'serie',
  'movers',
  'favoritos',
  'alertas',
  'calculadora',
  'comparador',
  'fuentes',
  'tendencias',
  'noticias',
] as const satisfies readonly WidgetTipo[];

export const MAX_WIDGETS = 60;
export const MAX_FAVORITOS = 200;
const MAX_TEXTO = 200;
const SIN_FECHA = '1970-01-01T00:00:00.000Z';

export interface PreferenciasConfigWire {
  schema: 1;
  perfil_aplicado: PerfilTipo | null;
  moneda: PreferenciasConfig['moneda'];
  bs_modo: PreferenciasConfig['bsModo'];
  rendimiento_canal_pct: number;
  rendimiento_por_categoria: Record<string, number>;
  home: { widgets: WidgetConfig[] };
  intereses: PreferenciasConfig['intereses'];
  favoritos: Favorito[];
  vista: PreferenciasConfig['vista'];
  csv_separador: PreferenciasConfig['csvSeparador'];
}

export function toWirePreferencias(c: PreferenciasConfig): PreferenciasConfigWire {
  return {
    schema: 1,
    perfil_aplicado: c.perfilAplicado,
    moneda: c.moneda,
    bs_modo: c.bsModo,
    rendimiento_canal_pct: c.rendimientoCanalPct,
    rendimiento_por_categoria: { ...c.rendimientoPorCategoria },
    home: {
      widgets: c.home.widgets.map((w) => ({
        id: w.id,
        tipo: w.tipo,
        visible: w.visible,
        params: { ...w.params },
      })),
    },
    intereses: {
      rubros: [...c.intereses.rubros],
      regiones: [...c.intereses.regiones],
      fuentes: [...c.intereses.fuentes],
    },
    favoritos: c.favoritos.map((f) => ({ ...f })),
    vista: c.vista,
    csv_separador: c.csvSeparador,
  };
}

/* ───────────── Saneamiento ───────────── */

const porcentaje = (v: unknown, porDefecto: number): number =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= 100 ? v : porDefecto;

const elegir = <T extends string>(v: unknown, validos: readonly T[], porDefecto: T): T =>
  typeof v === 'string' && (validos as readonly string[]).includes(v) ? (v as T) : porDefecto;

const perfilValido = normalizarPerfil;

const corto = z.string().trim().min(1).max(MAX_TEXTO);
const paramValor = z.union([z.string().max(MAX_TEXTO), z.number().finite(), z.boolean(), z.null()]);

function sanearParams(v: unknown): WidgetParams {
  const params: WidgetParams = {};
  for (const [clave, valor] of Object.entries(asRec(v)).slice(0, 30)) {
    const r = paramValor.safeParse(valor);
    if (r.success && clave.length <= 60) params[clave] = r.data;
  }
  return params;
}

const widgetSchema = z.object({
  id: corto,
  tipo: z.enum(WIDGET_TIPOS),
  visible: z.unknown(),
  params: z.unknown(),
});

function sanearWidgets(v: unknown, porDefecto: WidgetConfig[]): WidgetConfig[] {
  if (!isRec(v) || !Array.isArray(v.widgets)) return porDefecto;
  const widgets = v.widgets.flatMap((w): WidgetConfig[] => {
    const r = widgetSchema.safeParse(w);
    return r.success
      ? [
          {
            id: r.data.id,
            tipo: r.data.tipo,
            visible: r.data.visible !== false,
            params: sanearParams(r.data.params),
          },
        ]
      : [];
  });
  return sinDuplicados(widgets, (w) => w.id).slice(0, MAX_WIDGETS);
}

const favoritoSchema = z.object({
  id: corto,
  rubro: corto,
  categoria: corto,
  presentacion: z.unknown(),
  region: z.unknown(),
  fuente: z.unknown(),
  creado: z.unknown(),
});

function sanearFavoritos(v: unknown): Favorito[] {
  if (!Array.isArray(v)) return [];
  const favoritos = v.flatMap((f): Favorito[] => {
    const r = favoritoSchema.safeParse(f);
    if (!r.success) return [];
    const creado = toStr(r.data.creado);
    return [
      {
        id: r.data.id,
        rubro: r.data.rubro,
        categoria: r.data.categoria,
        presentacion: toStr(r.data.presentacion),
        region: toStr(r.data.region),
        fuente: toStr(r.data.fuente),
        creado: creado !== null && !Number.isNaN(Date.parse(creado)) ? creado : SIN_FECHA,
      },
    ];
  });
  return sinDuplicados(favoritos, (f) => f.id).slice(0, MAX_FAVORITOS);
}

function sanearRendimientos(v: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [categoria, valor] of Object.entries(asRec(v)).slice(0, 100)) {
    if (
      typeof valor === 'number' &&
      Number.isFinite(valor) &&
      valor > 0 &&
      valor <= 100 &&
      categoria.length <= 60
    )
      out[categoria] = valor;
  }
  return out;
}

const listaCorta = (v: unknown): string[] =>
  toStrList(v)
    .filter((t) => t.length <= MAX_TEXTO)
    .slice(0, 100);

/** Config del servidor → PreferenciasConfig saneada. null si ni siquiera es un objeto. */
export function adaptConfigWire(raw: unknown): PreferenciasConfig | null {
  if (!isRec(raw)) return null;
  const base = createDefaultPrefs();
  const intereses = asRec(raw.intereses);
  return {
    schema: 1,
    perfilAplicado: perfilValido(raw.perfil_aplicado),
    moneda: elegir(raw.moneda, ['USD', 'VES'] as const, base.moneda),
    bsModo: elegir(raw.bs_modo, ['actual', 'fecha_dato'] as const, base.bsModo),
    rendimientoCanalPct: porcentaje(raw.rendimiento_canal_pct, base.rendimientoCanalPct),
    rendimientoPorCategoria: sanearRendimientos(raw.rendimiento_por_categoria),
    home: { widgets: sanearWidgets(raw.home, base.home.widgets) },
    intereses: {
      rubros: listaCorta(intereses.rubros),
      regiones: listaCorta(intereses.regiones),
      fuentes: listaCorta(intereses.fuentes),
    },
    favoritos: sanearFavoritos(raw.favoritos),
    vista: elegir(raw.vista, ['tarjetas', 'tabla'] as const, base.vista),
    csvSeparador: elegir(raw.csv_separador, [',', ';'] as const, base.csvSeparador),
  };
}
